import { useState, useEffect } from "react";
import api from "@/lib/api";
import { formatINR } from "@/lib/constants";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Search, ArrowUpFromLine, CheckCircle2, Loader2, Calendar, Landmark, Receipt, ShoppingCart } from "lucide-react";
import { toast } from "sonner";

const sourceBadge = (source) =>
  source === "purchase"
    ? <Badge className="bg-[#fef3c7] text-[#92400e] gap-1"><ShoppingCart className="h-3 w-3" /> Purchase</Badge>
    : <Badge className="bg-[#e0e7ff] text-[#3730a3] gap-1"><Receipt className="h-3 w-3" /> Expense</Badge>;

export default function PayablesPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [payDialog, setPayDialog] = useState(null);
  const [payForm, setPayForm] = useState({ payment_date: "", bank_account: "" });
  const [marking, setMarking] = useState(false);

  const refresh = () => {
    setLoading(true);
    api.get("/payables")
      .then((r) => setRows(r.data || []))
      .catch(() => toast.error("Failed to load payables"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { refresh(); }, []);

  const markPaid = async () => {
    if (!payForm.bank_account.trim()) {
      toast.error("Please enter the bank account");
      return;
    }
    setMarking(true);
    try {
      // Dispatch to the correct endpoint based on the row's source collection
      const endpoint = payDialog.source === "purchase"
        ? `/purchases/${payDialog.id}/mark-paid`
        : `/expenses/${payDialog.id}/mark-paid`;
      await api.put(endpoint, {
        payment_date: payForm.payment_date || new Date().toISOString().split("T")[0],
        bank_account: payForm.bank_account,
      });
      setRows(rows.filter((r) => !(r.id === payDialog.id && r.source === payDialog.source)));
      toast.success(`${payDialog.source === "purchase" ? "Purchase" : "Expense"} to ${payDialog.vendor_name} marked as paid`);
      setPayDialog(null);
      setPayForm({ payment_date: "", bank_account: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to mark as paid");
    } finally {
      setMarking(false);
    }
  };

  const totalPayable = rows.reduce((s, r) => s + (r.amount_due ?? r.amount ?? 0), 0);

  const filtered = rows.filter((r) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return r.vendor_name?.toLowerCase().includes(q)
      || r.category?.toLowerCase().includes(q)
      || r.description?.toLowerCase().includes(q);
  });

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="payables-page">
      <div>
        <h1 className="font-outfit text-2xl font-bold text-[#0f172a] tracking-tight">Payables</h1>
        <p className="text-sm text-[#64748b] mt-1">
          {rows.length} unpaid bills &middot; Total: {formatINR(totalPayable)}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card className="bg-white border-[#e2e8f0] shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-[#ef4444]/10 flex items-center justify-center">
              <ArrowUpFromLine className="h-5 w-5 text-[#ef4444]" />
            </div>
            <div>
              <p className="text-xs text-[#64748b] font-medium">Total Payable</p>
              <p className="text-lg font-outfit font-bold text-[#0f172a]" data-testid="total-payable">{formatINR(totalPayable)}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-[#e2e8f0] shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-[#f59e0b]/10 flex items-center justify-center">
              <Receipt className="h-5 w-5 text-[#f59e0b]" />
            </div>
            <div>
              <p className="text-xs text-[#64748b] font-medium">Pending Bills</p>
              <p className="text-lg font-outfit font-bold text-[#0f172a]">{rows.length}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94a3b8]" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by vendor, category or description..."
          data-testid="payables-search"
          className="pl-9 h-10 border-[#e2e8f0]"
        />
      </div>

      <Card className="bg-white border-[#e2e8f0] shadow-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-sm text-[#64748b]">Loading...</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center">
              <CheckCircle2 className="h-12 w-12 text-[#10b981]/30 mx-auto mb-3" />
              <p className="text-sm text-[#64748b]">
                {search ? "No payables match your search." : "All bills are paid! Nothing owed."}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#e2e8f0] bg-[#f8fafc]">
                    <th className="text-left py-3 px-4 text-[#64748b] font-medium">Source</th>
                    <th className="text-left py-3 px-4 text-[#64748b] font-medium">Vendor</th>
                    <th className="text-left py-3 px-4 text-[#64748b] font-medium">Category</th>
                    <th className="text-left py-3 px-4 text-[#64748b] font-medium">Date</th>
                    <th className="text-left py-3 px-4 text-[#64748b] font-medium">Due Date</th>
                    <th className="text-right py-3 px-4 text-[#64748b] font-medium">Amount Due</th>
                    <th className="text-right py-3 px-4 text-[#64748b] font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => {
                    const isOverdue = r.due_date && new Date(r.due_date) < new Date();
                    const due = r.amount_due ?? r.amount;
                    return (
                      <tr key={`${r.source}-${r.id}`} className="border-b border-[#f1f5f9] last:border-0 hover:bg-[#f8fafc]" data-testid={`payable-row-${r.source}-${r.id}`}>
                        <td className="py-3 px-4">{sourceBadge(r.source)}</td>
                        <td className="py-3 px-4 text-[#0f172a] font-medium">{r.vendor_name || "—"}</td>
                        <td className="py-3 px-4 text-[#64748b]">{r.category || r.description || "-"}</td>
                        <td className="py-3 px-4 text-[#64748b]">{r.date}</td>
                        <td className={`py-3 px-4 ${isOverdue ? "text-red-500 font-medium" : "text-[#64748b]"}`}>
                          {r.due_date || "-"}
                        </td>
                        <td className="py-3 px-4 text-right font-mono font-semibold text-[#0f172a]">
                          {formatINR(due)}
                          {r.payment_status === "partially_paid" && r.amount_paid > 0 && (
                            <div className="text-[10px] text-[#94a3b8] font-normal">
                              of {formatINR(r.amount)} ({formatINR(r.amount_paid)} paid)
                            </div>
                          )}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <Button
                            size="sm"
                            onClick={() => {
                              setPayDialog(r);
                              setPayForm({ payment_date: new Date().toISOString().split("T")[0], bank_account: "" });
                            }}
                            data-testid={`mark-payable-paid-${r.source}-${r.id}`}
                            className="bg-[#10b981] hover:bg-[#059669] text-white text-xs rounded-lg h-7 px-2.5"
                          >
                            <CheckCircle2 className="h-3 w-3 mr-1" /> Paid
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!payDialog} onOpenChange={(o) => !o && setPayDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl">Record Payment</DialogTitle>
            <DialogDescription className="text-sm text-[#64748b]">
              Mark {payDialog?.source === "purchase" ? "purchase" : "expense"} to {payDialog?.vendor_name} as paid
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="bg-[#f8fafc] rounded-lg p-3 border border-[#e2e8f0]">
              <div className="flex justify-between items-center text-sm">
                <span className="text-[#64748b]">Source</span>
                {payDialog && sourceBadge(payDialog.source)}
              </div>
              <div className="flex justify-between text-sm mt-1">
                <span className="text-[#64748b]">Vendor</span>
                <span className="text-[#0f172a] font-medium">{payDialog?.vendor_name}</span>
              </div>
              <div className="flex justify-between text-sm mt-1">
                <span className="text-[#64748b]">Amount Due</span>
                <span className="font-mono font-bold text-[#ef4444]">{formatINR(payDialog?.amount_due ?? payDialog?.amount)}</span>
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5" /> Payment Date
              </Label>
              <Input
                type="date"
                value={payForm.payment_date}
                onChange={(e) => setPayForm({ ...payForm, payment_date: e.target.value })}
                data-testid="payable-date-input"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label className="text-sm font-medium flex items-center gap-1.5">
                <Landmark className="h-3.5 w-3.5" /> Bank Account *
              </Label>
              <Input
                value={payForm.bank_account}
                onChange={(e) => setPayForm({ ...payForm, bank_account: e.target.value })}
                placeholder="e.g., HDFC Current, ICICI Savings"
                data-testid="payable-bank-input"
                className="mt-1.5"
              />
            </div>
            <Button
              onClick={markPaid}
              disabled={marking}
              data-testid="confirm-payable-paid-btn"
              className="w-full bg-[#10b981] hover:bg-[#059669] text-white rounded-xl"
            >
              {marking ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
              {marking ? "Processing..." : "Confirm Payment Made"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
